import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, apiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Bot, Send, Loader2, ShieldAlert, FileText, Sparkles } from "lucide-react";

const SUGGESTS = [
  "Quali sono le principali esclusioni di questa polizza?",
  "Cosa prevede la garanzia in caso di sinistro?",
  "Ci sono franchigie o scoperti applicati?",
  "Quali sono i massimali previsti?",
];

export default function TechAssistant() {
  const [params] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [product, setProduct] = useState(params.get("product") || "");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `sess-${Date.now()}`);
  const scrollRef = useRef(null);

  useEffect(() => {
    api.get("/kb/products").then(({ data }) => {
      setProducts(data);
      if (!product && data.length) setProduct((data.find((p) => p.kb_ready) || data[0]).code);
    });
  }, []);

  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages, loading]);
  useEffect(() => { setMessages([]); }, [product]);

  const currentProduct = products.find((p) => p.code === product);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || !product || loading) return;
    setMessages((m) => [...m, { role: "user", text: q }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/kb/chat", { product_code: product, question: q, session_id: sessionId });
      setMessages((m) => [...m, { role: "assistant", text: data.answer, sources: data.sources, grounded: data.grounded }]);
    } catch (e) {
      toast.error(apiError(e));
      setMessages((m) => [...m, { role: "assistant", text: "Si è verificato un errore. Riprova.", sources: [] }]);
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="flex items-center gap-2 font-heading text-3xl font-semibold text-forest">
          <Bot className="h-7 w-7 text-terracotta" /> Assistente tecnico
        </h1>
        <p className="mt-1 text-muted-foreground">Domande sulle coperture: risposte basate esclusivamente sui documenti di polizza (CGA, DIP, DIP Aggiuntivo) del prodotto scelto.</p>
      </div>

      <div className="flex items-start gap-2.5 rounded-xl border border-terracotta/25 bg-[#FBF6F3] p-3.5 text-xs leading-relaxed text-forest/90" data-testid="assistant-ai-banner">
        <Bot className="mt-0.5 h-4 w-4 shrink-0 text-terracotta-hover" />
        <span>
          <b>Stai interagendo con un assistente basato su Intelligenza Artificiale.</b> Le risposte sono generate elaborando esclusivamente
          le Condizioni di polizza e non costituiscono consulenza personalizzata né parere definitivo. Nessuna decisione sul cliente è assunta
          in modo automatizzato. Verifica sempre le CGA ufficiali prima di fornire indicazioni al cliente.
        </span>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <Select value={product} onValueChange={setProduct}>
          <SelectTrigger data-testid="assistant-product" className="sm:w-96"><SelectValue placeholder="Scegli il prodotto..." /></SelectTrigger>
          <SelectContent>
            {products.map((p) => (
              <SelectItem key={p.code} value={p.code} disabled={!p.kb_ready}>
                {p.name}{!p.kb_ready ? " (documenti non indicizzati)" : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {currentProduct?.kb_ready && (
          <span className="flex items-center gap-1.5 text-xs text-emerald-600"><FileText className="h-3.5 w-3.5" /> Fonti indicizzate</span>
        )}
      </div>

      <Card className="flex h-[60vh] flex-col overflow-hidden rounded-xl border-border/60">
        <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto p-5" data-testid="assistant-messages">
          {messages.length === 0 && (
            <div className="flex h-full flex-col items-center justify-center text-center">
              <Sparkles className="mb-3 h-8 w-8 text-terracotta/60" />
              <p className="max-w-md text-sm text-muted-foreground">Fai una domanda tecnica sul prodotto selezionato. Esempi:</p>
              <div className="mt-4 flex flex-wrap justify-center gap-2">
                {SUGGESTS.map((s, i) => (
                  <button key={i} data-testid={`assistant-suggest-${i}`} onClick={() => send(s)} disabled={!currentProduct?.kb_ready}
                    className="rounded-full border border-border px-3 py-1.5 text-xs text-forest/80 transition-colors hover:bg-sage disabled:opacity-40">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm ${m.role === "user" ? "bg-forest text-white" : "bg-sage/60 text-forest"}`}>
                <div className="whitespace-pre-wrap leading-relaxed">{m.text}</div>
                {m.role === "assistant" && m.sources?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {m.sources.map((s) => <span key={s} className="rounded-full bg-white/70 px-2 py-0.5 text-[10px] font-medium text-terracotta">Fonte: {s}</span>)}
                  </div>
                )}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start"><div className="flex items-center gap-2 rounded-2xl bg-sage/60 px-4 py-2.5 text-sm text-forest"><Loader2 className="h-4 w-4 animate-spin" /> Consulto le condizioni di polizza…</div></div>
          )}
        </div>

        <div className="border-t border-border p-3">
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2">
            <Input data-testid="assistant-input" value={input} onChange={(e) => setInput(e.target.value)}
              placeholder={currentProduct?.kb_ready ? "Scrivi la tua domanda tecnica…" : "Seleziona un prodotto con documenti indicizzati"}
              disabled={!currentProduct?.kb_ready || loading} />
            <Button type="submit" data-testid="assistant-send" disabled={!input.trim() || !currentProduct?.kb_ready || loading} className="gap-1.5 bg-terracotta hover:bg-terracotta-hover">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </form>
          <p className="mt-2 flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <ShieldAlert className="h-3 w-3" /> Assistente AI: risposte basate solo sulle condizioni di polizza allegate. Verifica sempre sulle CGA prima di indicazioni definitive.
          </p>
        </div>
      </Card>
    </div>
  );
}
